import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const SystemHealth = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [debug, setDebug] = useState(null);

  const checkHealth = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/health`, { timeout: 5000 });
      setHealth(response.data);
    } catch (error) {
      setHealth({ 
        status: 'unhealthy', 
        error: error.message,
        database: 'disconnected' 
      });
    } finally {
      setLoading(false);
    }
  };

  const checkDebug = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/debug/connection`, { timeout: 5000 });
      setDebug(response.data);
    } catch (error) {
      setDebug({ 
        status: '❌ Connection Failed', 
        error: error.message 
      });
    }
  };

  useEffect(() => {
    checkHealth();
    checkDebug();
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
          System Health Monitor
        </h1>
        <Button onClick={() => { checkHealth(); checkDebug(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Health Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {health?.status === 'healthy' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            API Health Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">
              <div className="loading-spinner mx-auto"></div>
              <p className="mt-2 text-gray-600">Checking health...</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium">Status:</span>
                <span className={health?.status === 'healthy' ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {health?.status || 'Unknown'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="font-medium">Database:</span>
                <span className={health?.database === 'connected' ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {health?.database || 'Unknown'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium">Gmail API:</span>
                <span className={health?.gmail_status === 'healthy' ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {health?.gmail_status === 'healthy' ? 'Connected' : health?.gmail_status === 'unhealthy' ? 'Error / Expired' : 'Unknown'}
                </span>
              </div>

              {health?.gmail_error && (
                <div className="mt-2 p-3 bg-amber-50 rounded-lg text-amber-700">
                  <p className="font-medium">Gmail API Issue:</p>
                  <p className="text-sm">{health.gmail_error}</p>
                </div>
              )}

              {health?.stats && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium mb-2">Database Statistics:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Users: {health.stats.users}</div>
                    <div>Students: {health.stats.students}</div>
                  </div>
                </div>
              )}

              {health?.error && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg text-red-700">
                  <p className="font-medium">Error:</p>
                  <p className="text-sm">{health.error}</p>
                </div>
              )}

              {health?.timestamp && (
                <p className="text-xs text-gray-500 mt-2">
                  Last checked: {new Date(health.timestamp).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Debug Info */}
      {debug && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🔧 Debug Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium">Connection Status:</span>
                <span className={debug.status?.includes('✅') ? 'text-green-600' : 'text-red-600'}>
                  {debug.status}
                </span>
              </div>

              {debug.mongodb_version && (
                <div className="flex justify-between items-center">
                  <span className="font-medium">MongoDB Version:</span>
                  <span>{debug.mongodb_version}</span>
                </div>
              )}

              {debug.database_name && (
                <div className="flex justify-between items-center">
                  <span className="font-medium">Database:</span>
                  <span>{debug.database_name}</span>
                </div>
              )}

              {debug.collections && (
                <div className="mt-4">
                  <p className="font-medium mb-2">Collections:</p>
                  <div className="p-3 bg-gray-50 rounded text-sm">
                    {debug.collections.join(', ')}
                  </div>
                </div>
              )}

              {debug.collection_counts && (
                <div className="mt-4">
                  <p className="font-medium mb-2">Document Counts:</p>
                  <div className="p-3 bg-gray-50 rounded">
                    <pre className="text-xs overflow-auto">
                      {JSON.stringify(debug.collection_counts, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {debug.error && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg text-red-700">
                  <p className="font-medium">Error Details:</p>
                  <p className="text-sm break-words">{debug.error}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backend URL Info */}
      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="font-medium">Backend URL:</span>
              <span className="font-mono text-xs">{API_URL}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">API Endpoint:</span>
              <span className="font-mono text-xs">{API_URL}/api</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealth;
